import { ApolloClient, ApolloLink, InMemoryCache, HttpLink } from "@apollo/client";
import { ErrorLink } from "@apollo/client/link/error";
import { map } from "rxjs/operators";
import get from "lodash/get";
import authService, { getFingerprint, setFingerprint, persistCognitoToken } from "./services/authService";
import { CombinedGraphQLErrors } from "@apollo/client";

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_ACL_BFF_URL,
  credentials: "include",
});

const authMiddleware = new ApolloLink((operation, forward) => {
  try {
    const cognitoToken = authService.fetchCognitoTokens();

    const token = authService.getRaweResultsToken();
    const isCognito = authService.isCognito();
    if (token && token != "undefined" && !isCognito) {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          authorization: `Bearer ${token}`,
          "x-client-fgp": getFingerprint(),
        },
        fetchOptions: {
          credentials: "include",
        },
      }));
    }
    if (cognitoToken && isCognito) {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          "cognito-access-token": cognitoToken.accessToken,
          "cognito-refresh-token": cognitoToken.refreshToken,
          "cognito-idtoken": cognitoToken.idToken,
        },
        fetchOptions: {
          credentials: "include",
        },
      }));
    } else {
      // Auto-login: only refresh token available (no access/id tokens yet)
      const refreshTokenOnly = sessionStorage.getItem("cognito-refresh-token");
      if (refreshTokenOnly) {
        operation.setContext(({ headers = {} }) => ({
          headers: {
            ...headers,
            "cognito-refresh-token": refreshTokenOnly,
          },
          fetchOptions: {
            credentials: "include",
          },
        }));
      }
    }
  } catch (err) {
    console.error("Error updating request", err);
  }

  return forward(operation);
});

const errorHandler = new ErrorLink((errorResponse: any) => {
  const { graphQLErrors, error } = errorResponse;
  let redirectPath = "/login";
  //Retain if it still works.
  if (graphQLErrors) {
    const errorCode = get(graphQLErrors, "[0].extensions.code");

    if (errorCode === "UNAUTHENTICATED") {
      const isCognito = authService.isCognito();
      authService.clearAll();
      if (isCognito) {
        redirectPath = "/login-sso";
      }

      if (window.location.pathname !== redirectPath) {
        window.location.href = redirectPath;
      }
    }
  }
  //New Approach
  if (error) {
    if (CombinedGraphQLErrors.is(error)) {
      const foundErrors = error.errors.map((e) => e.extensions?.code);
      if (foundErrors.includes("UNAUTHENTICATED")) {
        const isCognito = authService.isCognito();
        authService.clearAll();
        if (isCognito) {
          redirectPath = "/login-sso";
        }

        if (window.location.pathname !== redirectPath) {
          window.location.href = redirectPath;
        }
      }
    }
  }
});

const responseLink = new ApolloLink((operation, forward) => {
  return forward(operation).pipe(
    map((data) => {
      const context = operation.getContext();
      const headers = context?.response?.headers;

      if (headers) {
        const fingerprint = headers.get("x-client-fgp");
        const newAccessTokenHeader = headers.get("newaccesstoken");
        const newIdTokenHeader = headers.get("newidtoken");
        if (newIdTokenHeader && newAccessTokenHeader) {
          persistCognitoToken(newAccessTokenHeader, newIdTokenHeader);
        }
        if (fingerprint) {
          setFingerprint(fingerprint);
        }
      }
      return data;
    }),
  );
});

const removeTypenameLink = new ApolloLink((operation, forward) => {
  const operationsToClean = ["SearchEpisodes", "GetEpisodes", "UpdateUser", "AddUser"];

  if (operation.operationName && operationsToClean.includes(operation.operationName)) {
    const removeTypename = (obj: any): any => {
      if (obj === null || typeof obj !== "object") return obj;
      if (Array.isArray(obj)) return obj.map(removeTypename);

      const { __typename, ...rest } = obj;
      const cleaned: any = {};
      for (const key in rest) {
        cleaned[key] = removeTypename(rest[key]);
      }
      return cleaned;
    };

    operation.variables = removeTypename(operation.variables);
  }

  return forward(operation);
});

export const client = new ApolloClient({
  link: authMiddleware.concat(errorHandler).concat(removeTypenameLink).concat(responseLink).concat(httpLink),
  cache: new InMemoryCache({
    typePolicies: {
      Patient: {
        keyFields: ["fullnameTitleCase", "dob"],
      },
    },
  }),
});
